import {
  OppvarmingBioenergiFormData,
  OppvarmingBruk,
  OppvarmingElektriskFormData,
  OppvarmingFjernvarmeFormData,
  OppvarmingFormData,
  OppvarmingGassFormData,
  OppvarmingOljeFormData,
  OppvarmingVarmepumpeFormData,
  OppvarmingVedFormData,
  ValuedCheckbox,
  VannbarenVarme,
  Varmefordeling,
} from "../types";
import {
  KjelKaminVannbarenVarme,
  OppvarmingBioenergi,
  OppvarmingElektrisk,
  OppvarmingFjernvarme,
  OppvarmingGass,
  OppvarmingOlje,
  OppvarmingSol,
  OppvarmingVarmepumpe,
  OppvarmingVed,
  TTermostatstyring,
  UserInput,
} from "../../../../../types/registration/userInput";
import {
  JaNeiVetIkke,
  Termostatstyring,
} from "../../../../../components/registering/utils/registerEnums";

// Utility functions to map between form data and UserInput data structures
// TODO: Clean up this mess
const formToUserInputKjel = (
  data?: VannbarenVarme,
): KjelKaminVannbarenVarme | undefined => {
  if (!data) {
    return undefined;
  }

  return {
    gulvvarme: data.gulvvarme?.selected
      ? data.gulvvarme?.value?.termostatstyring
      : undefined,
    radiator: data.radiator?.selected
      ? data.radiator?.value?.termostatstyring
      : undefined,
    varmesystemBruk: oppvarmingBrukToUserInput(data.bruk),
    installasjonsAr: data.installasjonsAr,
    vetInstallasjonsAr: data.vetInstallasjonsAr,
    varmesentral: data.varmesentral?.varmesentralType,
    varmesentralInstallasjonsAr: data.varmesentral?.installasjonsAr,
    vetVarmesentralInstallasjonsAr: data.varmesentral?.vetInstallasjonsAr,
    varmesentralInstallertIOppvarmetRom:
      data.varmesentral?.installertIOppvarmetRom === JaNeiVetIkke.Ja,
    serviceGjennomfortSiste2Ar:
      data.serviceGjennomfortSiste2Ar || JaNeiVetIkke.VetIkke,
  };
};

const toTermostatstyringCheckbox = (
  value?: Termostatstyring,
): ValuedCheckbox<TTermostatstyring> => ({
  value: value
    ? {
      termostatstyring: value,
    }
    : undefined,
  selected: !!value,
});

const userInputToFormKjel = (
  data?: KjelKaminVannbarenVarme,
): VannbarenVarme | undefined => {
  if (!data) {
    return undefined;
  }

  return {
    gulvvarme: toTermostatstyringCheckbox(data.gulvvarme),
    radiator: toTermostatstyringCheckbox(data.radiator),
    bruk: userInputToOppvarmingBruk(data?.varmesystemBruk),
    installasjonsAr: data.installasjonsAr,
    vetInstallasjonsAr: data.vetInstallasjonsAr,
    varmesentral: data.varmesentral
      ? {
        varmesentralType: data.varmesentral,
        installasjonsAr: data.varmesentralInstallasjonsAr,
        vetInstallasjonsAr: data.vetVarmesentralInstallasjonsAr,
        installertIOppvarmetRom: data.varmesentralInstallertIOppvarmetRom
          ? JaNeiVetIkke.Ja
          : JaNeiVetIkke.Nei,
      }
      : undefined,
    serviceGjennomfortSiste2Ar:
      data.serviceGjennomfortSiste2Ar || JaNeiVetIkke.VetIkke,
  };
};

export const fromOppvarmingFormDataToUserInput = (
  data: OppvarmingFormData,
  userInput: UserInput,
) => {
  const oppvarmingElektrisk: OppvarmingElektrisk = {
    panelovn: data.elektrisk?.value?.panelovn?.selected
      ? data.elektrisk?.value?.panelovn?.value
      : undefined,
    gulvvarme: data.elektrisk?.value?.gulvvarme?.selected
      ? data.elektrisk?.value?.gulvvarme?.value
      : undefined,
    takvarme: data.elektrisk?.value?.takvarme?.selected
      ? data.elektrisk?.value?.takvarme?.value
      : undefined,
    luftvarme: data.elektrisk?.value?.luftvarme?.selected
      ? data.elektrisk?.value?.luftvarme?.value
      : undefined,
    kjel: data.elektrisk?.value?.kjel?.selected
      ? formToUserInputKjel(data.elektrisk?.value?.kjel?.value)
      : undefined,
    elektriskBereder:
      data.elektrisk?.value?.varmtvannsbereder?.selected &&
        data.elektrisk?.value?.varmtvannsbereder?.value
        ? {
          installasjonsAr:
            data.elektrisk?.value?.varmtvannsbereder?.value?.installasjonsAr,
          vetInstallasjonsAr: data.elektrisk?.value?.varmtvannsbereder?.value?.vetInstallasjonsAr,
          installertIOppvarmetRom:
            data.elektrisk?.value?.varmtvannsbereder?.value
              ?.installertIOppvarmetRom === JaNeiVetIkke.Ja,
        }
        : undefined,
  };

  const oppvarmingBioenergi: OppvarmingBioenergi = {
    kamin: data.bioenergi?.value?.kamin?.selected
      ? data.bioenergi?.value?.kamin?.value
      : undefined,
    kaminMedVannbarenVarme: data.bioenergi?.value?.kaminMedVannbarenVarme
      ?.selected
      ? formToUserInputKjel(
        data.bioenergi?.value?.kaminMedVannbarenVarme?.value,
      )
      : undefined,
    kjel: data.bioenergi?.value?.kjel?.selected
      ? formToUserInputKjel(data.bioenergi?.value?.kjel?.value)
      : undefined,
  };

  const oppvarmingOlje: OppvarmingOlje = {
    kjel: data.olje?.value?.kjel?.selected
      ? formToUserInputKjel(data.olje?.value?.kjel?.value)
      : undefined,
    kamin: data.olje?.value?.kamin?.selected
      ? data.olje?.value?.kamin?.value
      : undefined,
  };

  const oppvarmingGass: OppvarmingGass = {
    kjel: data.gass?.value?.kjel?.selected
      ? formToUserInputKjel(data.gass?.value?.kjel?.value)
      : undefined,
    kamin: data.gass?.value?.kamin?.selected
      ? data.gass?.value?.kamin?.value
      : undefined,
  };

  const existingOppvarmingSol = userInput.tekniskUtstyr?.oppvarmingSol;

  const oppvarmingSolFromForm = (): OppvarmingSol | undefined => {
    if (!data.solfanger?.selected) {
      return undefined;
    }

    return {
      ...existingOppvarmingSol,
      solfangerMedVannbarenVarmeMedVarmesentral: formToUserInputKjel(data.solfanger?.value),
    };
  };

  const varmepumpeFordeling = data.varmepumpe?.value?.varmefordeling;

  const oppvarmingVarmepumpe: OppvarmingVarmepumpe = {
    vannbarenVarmeMedVarmesentral:
      data.varmepumpe?.selected && varmepumpeFordeling === "vannbarenVarme"
        ? formToUserInputKjel(data.varmepumpe?.value?.vannbarenVarme)
        : undefined,
    punktoppvarming:
      data.varmepumpe?.selected && varmepumpeFordeling === "punktoppvarming"
        ? data.varmepumpe?.value?.punktoppvarming
        : undefined,
    varmeKilde: data.varmepumpe?.selected
      ? data.varmepumpe?.value?.varmekilde
      : undefined,
  };

  const oppvarmingVed: OppvarmingVed = {
    installasjonsArApenPeis: data.ved?.value?.apenPeis?.selected
      ? data.ved?.value?.apenPeis?.value?.installasjonsAr
      : undefined,
    vetInstallasjonsArApenPeis: data.ved?.value?.apenPeis?.selected
      ? data.ved?.value?.apenPeis?.value?.vetInstallasjonsAr
      : undefined,
    installasjonsArLukketOvn: data.ved?.value?.lukketOvn?.selected
      ? data.ved?.value?.lukketOvn?.value?.installasjonsAr
      : undefined,
    vetInstallasjonsArLukketOvn: data.ved?.value?.lukketOvn?.selected
      ? data.ved?.value?.lukketOvn?.value?.vetInstallasjonsAr
      : undefined,
    installasjonsArPeisMedDor: data.ved?.value?.peisMedDor?.selected
      ? data.ved?.value?.peisMedDor?.value?.installasjonsAr
      : undefined,
    vetInstallasjonsArPeisMedDor: data.ved?.value?.peisMedDor?.selected
      ? data.ved?.value?.peisMedDor?.value?.vetInstallasjonsAr
      : undefined,
  };

  const oppvarmingFjernvarme: OppvarmingFjernvarme = {
    gulvvarme: data.fjernvarme?.value?.gulvvarme?.selected
      ? data.fjernvarme?.value?.gulvvarme?.value?.termostatstyring
      : undefined,
    radiator: data.fjernvarme?.value?.radiator?.selected
      ? data.fjernvarme?.value?.radiator?.value?.termostatstyring
      : undefined,
    varmesystemBruk: oppvarmingBrukToUserInput(data.fjernvarme?.value?.bruk),
    varmesentralInstallasjonsAr:
      data.fjernvarme?.value?.varmesentral?.installasjonsAr,
    vetVarmesentralInstallasjonsAr:
      data.fjernvarme?.value?.varmesentral?.vetInstallasjonsAr,
    varmesentralInstallertIOppvarmetRom:
      data.fjernvarme?.value?.varmesentral?.installertIOppvarmetRom ===
      JaNeiVetIkke.Ja,
  };

  const updatedUserInput: UserInput = {
    ...userInput,
    tekniskUtstyr: {
      oppvarmingElektrisk: data.elektrisk?.selected
        ? oppvarmingElektrisk
        : undefined,
      oppvarmingBioenergi: data.bioenergi?.selected
        ? oppvarmingBioenergi
        : undefined,
      oppvarmingOlje: data.olje?.selected ? oppvarmingOlje : undefined,
      oppvarmingGass: data.gass?.selected ? oppvarmingGass : undefined,
      oppvarmingSol: oppvarmingSolFromForm(),
      oppvarmingVarmepumpe: data.varmepumpe?.selected
        ? oppvarmingVarmepumpe
        : undefined,
      oppvarmingVed: data.ved?.selected ? oppvarmingVed : undefined,
      oppvarmingFjernvarme: data.fjernvarme?.selected
        ? oppvarmingFjernvarme
        : undefined,
    },
  };

  return updatedUserInput;
};

export const fromUserInputToOppvarmingFormData = (
  userInput: UserInput,
): OppvarmingFormData => {
  const tekniskUtstyr = userInput.tekniskUtstyr;

  const elektrisk: ValuedCheckbox<OppvarmingElektriskFormData> = {
    selected: !!tekniskUtstyr?.oppvarmingElektrisk,
    value: {
      panelovn: {
        value: tekniskUtstyr?.oppvarmingElektrisk?.panelovn,
        selected: !!tekniskUtstyr?.oppvarmingElektrisk?.panelovn,
      },
      gulvvarme: {
        value: tekniskUtstyr?.oppvarmingElektrisk?.gulvvarme,
        selected: !!tekniskUtstyr?.oppvarmingElektrisk?.gulvvarme,
      },
      takvarme: {
        value: tekniskUtstyr?.oppvarmingElektrisk?.takvarme,
        selected: !!tekniskUtstyr?.oppvarmingElektrisk?.takvarme,
      },
      luftvarme: {
        value: tekniskUtstyr?.oppvarmingElektrisk?.luftvarme,
        selected: !!tekniskUtstyr?.oppvarmingElektrisk?.luftvarme,
      },
      kjel: {
        value: userInputToFormKjel(tekniskUtstyr?.oppvarmingElektrisk?.kjel),
        selected: !!tekniskUtstyr?.oppvarmingElektrisk?.kjel,
      },
      varmtvannsbereder: {
        value: tekniskUtstyr?.oppvarmingElektrisk?.elektriskBereder
          ? {
            installasjonsAr:
              tekniskUtstyr?.oppvarmingElektrisk?.elektriskBereder
                ?.installasjonsAr,
            vetInstallasjonsAr:
              tekniskUtstyr?.oppvarmingElektrisk?.elektriskBereder
                ?.vetInstallasjonsAr,
            installertIOppvarmetRom: tekniskUtstyr?.oppvarmingElektrisk
              ?.elektriskBereder?.installertIOppvarmetRom
              ? JaNeiVetIkke.Ja
              : JaNeiVetIkke.Nei,
          }
          : undefined,
        selected: !!tekniskUtstyr?.oppvarmingElektrisk?.elektriskBereder,
      },
    },
  };

  const bioenergi: ValuedCheckbox<OppvarmingBioenergiFormData> = {
    value: {
      kjel: {
        value: userInputToFormKjel(tekniskUtstyr?.oppvarmingBioenergi?.kjel),
        selected: !!tekniskUtstyr?.oppvarmingBioenergi?.kjel,
      },
      kaminMedVannbarenVarme: {
        value: userInputToFormKjel(
          tekniskUtstyr?.oppvarmingBioenergi?.kaminMedVannbarenVarme,
        ),
        selected: !!tekniskUtstyr?.oppvarmingBioenergi?.kaminMedVannbarenVarme,
      },
      kamin: {
        value: tekniskUtstyr?.oppvarmingBioenergi?.kamin,
        selected: !!tekniskUtstyr?.oppvarmingBioenergi?.kamin,
      },
    },
    selected: !!tekniskUtstyr?.oppvarmingBioenergi,
  };

  const olje: ValuedCheckbox<OppvarmingOljeFormData> = {
    value: {
      kjel: {
        value: userInputToFormKjel(tekniskUtstyr?.oppvarmingOlje?.kjel),
        selected: !!tekniskUtstyr?.oppvarmingOlje?.kjel,
      },
      kamin: {
        value: tekniskUtstyr?.oppvarmingOlje?.kamin,
        selected: !!tekniskUtstyr?.oppvarmingOlje?.kamin,
      },
    },
    selected: !!tekniskUtstyr?.oppvarmingOlje,
  };

  const gass: ValuedCheckbox<OppvarmingGassFormData> = {
    value: {
      kjel: {
        value: userInputToFormKjel(tekniskUtstyr?.oppvarmingGass?.kjel),
        selected: !!tekniskUtstyr?.oppvarmingGass?.kjel,
      },
      kamin: {
        value: tekniskUtstyr?.oppvarmingGass?.kamin,
        selected: !!tekniskUtstyr?.oppvarmingGass?.kamin,
      },
    },
    selected: !!tekniskUtstyr?.oppvarmingGass,
  };

  const oppvarmingSol = tekniskUtstyr?.oppvarmingSol;
  const hasSolfangerData = !!(
    oppvarmingSol?.solfangerMedVannbarenVarmeMedVarmesentral ||
    oppvarmingSol?.solfangerMedVannbarenVarme
  );

  const solfanger: ValuedCheckbox<VannbarenVarme> = {
    value: userInputToFormKjel(oppvarmingSol?.solfangerMedVannbarenVarmeMedVarmesentral),
    selected: hasSolfangerData,
  };

  const varmepumpeFordeling = (
    data?: OppvarmingVarmepumpe,
  ): Varmefordeling | undefined => {
    if (data?.vannbarenVarmeMedVarmesentral) {
      return "vannbarenVarme";
    }
    if (data?.punktoppvarming) {
      return "punktoppvarming";
    }
    return undefined;
  };

  const varmepumpe: ValuedCheckbox<OppvarmingVarmepumpeFormData> = {
    value: {
      vannbarenVarme: userInputToFormKjel(
        tekniskUtstyr?.oppvarmingVarmepumpe?.vannbarenVarmeMedVarmesentral,
      ),
      punktoppvarming: tekniskUtstyr?.oppvarmingVarmepumpe?.punktoppvarming,
      varmekilde: tekniskUtstyr?.oppvarmingVarmepumpe?.varmeKilde,
      varmefordeling: varmepumpeFordeling(tekniskUtstyr?.oppvarmingVarmepumpe),
    },
    selected: !!tekniskUtstyr?.oppvarmingVarmepumpe,
  };

  const ved: ValuedCheckbox<OppvarmingVedFormData> = {
    value: {
      apenPeis: {
        value: {
          installasjonsAr:
            tekniskUtstyr?.oppvarmingVed?.installasjonsArApenPeis,
          vetInstallasjonsAr: tekniskUtstyr?.oppvarmingVed?.vetInstallasjonsArApenPeis,
        },
        selected: !!tekniskUtstyr?.oppvarmingVed?.installasjonsArApenPeis,
      },
      lukketOvn: {
        value: {
          installasjonsAr:
            tekniskUtstyr?.oppvarmingVed?.installasjonsArLukketOvn,
          vetInstallasjonsAr: tekniskUtstyr?.oppvarmingVed?.vetInstallasjonsArLukketOvn,
        },
        selected: !!tekniskUtstyr?.oppvarmingVed?.installasjonsArLukketOvn,
      },
      peisMedDor: {
        value: {
          installasjonsAr:
            tekniskUtstyr?.oppvarmingVed?.installasjonsArPeisMedDor,
          vetInstallasjonsAr: tekniskUtstyr?.oppvarmingVed?.vetInstallasjonsArPeisMedDor,
        },
        selected: !!tekniskUtstyr?.oppvarmingVed?.installasjonsArPeisMedDor,
      },
    },
    selected: !!tekniskUtstyr?.oppvarmingVed,
  };

  const fjernvarme: ValuedCheckbox<OppvarmingFjernvarmeFormData> = {
    value: {
      bruk: userInputToOppvarmingBruk(
        tekniskUtstyr?.oppvarmingFjernvarme?.varmesystemBruk,
      ),
      varmesentral: {
        installasjonsAr:
          tekniskUtstyr?.oppvarmingFjernvarme?.varmesentralInstallasjonsAr,
        vetInstallasjonsAr:
          tekniskUtstyr?.oppvarmingFjernvarme?.vetVarmesentralInstallasjonsAr,
        installertIOppvarmetRom: tekniskUtstyr?.oppvarmingFjernvarme
          ?.varmesentralInstallertIOppvarmetRom
          ? JaNeiVetIkke.Ja
          : JaNeiVetIkke.Nei,
      },
      gulvvarme: toTermostatstyringCheckbox(
        tekniskUtstyr?.oppvarmingFjernvarme?.gulvvarme,
      ),
      radiator: toTermostatstyringCheckbox(
        tekniskUtstyr?.oppvarmingFjernvarme?.radiator,
      ),
    },
    selected: !!tekniskUtstyr?.oppvarmingFjernvarme,
  };

  return {
    elektrisk,
    bioenergi,
    olje,
    gass,
    solfanger,
    varmepumpe,
    ved,
    fjernvarme,
  };
};

enum VarmesystemBruk {
  Ukjent = "ukjent",
  Romoppvarming = "romoppvarming",
  Tappevann = "tappevann",
  VentilasjonVarmebatteri = "ventilasjonsvarme",
}

export const oppvarmingBrukToUserInput = (bruk?: OppvarmingBruk): string => {
  const selection: VarmesystemBruk[] = [];

  if (bruk?.ukjent) selection.push(VarmesystemBruk.Ukjent);
  if (bruk?.romoppvarming) selection.push(VarmesystemBruk.Romoppvarming);
  if (bruk?.tappevann) selection.push(VarmesystemBruk.Tappevann);
  if (bruk?.ventilasjonVarmebatteri)
    selection.push(VarmesystemBruk.VentilasjonVarmebatteri);

  return selection.join(",");
};

// Helper function to convert bitwise enum to boolean object (for loading data)
export const userInputToOppvarmingBruk = (value?: string): OppvarmingBruk => {
  return {
    romoppvarming: !!value?.includes(
      VarmesystemBruk.Romoppvarming.toLowerCase(),
    ),
    tappevann: !!value?.includes(VarmesystemBruk.Tappevann.toLowerCase()),
    ventilasjonVarmebatteri: !!value?.includes(
      VarmesystemBruk.VentilasjonVarmebatteri.toLowerCase(),
    ),
    ukjent: !!value?.includes(VarmesystemBruk.Ukjent.toLowerCase()),
  };
};
